import _ from "lodash";

export const SEARCH_MIN_LENGTH = 3;
export const SEARCH_MAX_LENGTH = 256;
export const CONSOLE_OBJECTS_PREVIEW_LIMIT = 3;
export const CONSOLE_OBJECTS_SEE_ALL_LIMIT = 15;
export const CONSOLE_OBJECTS_TYPE_SEE_ALL_LIMIT = 50;

const OBJECT_TYPE_LABELS = {
    fleet: "Fleet",
    cluster: "System",
    system: "System",
    svm: "Storage VM",
    storagevm: "Storage VM",
    vserver: "Storage VM",
    volume: "Volume",
    lun: "LUN",
    aggregate: "Aggregate",
    node: "Node"
};

export const getEmptyConsoleObjects = () => ({
    results: [],
    count: 0,
    limit: CONSOLE_OBJECTS_PREVIEW_LIMIT,
    error: false
});

export const ONTAP_OBJECT_TYPE_FILTERS = [
    { label: "Fleet", labelKey: "fleet", apiType: "FLEET" },
    { label: "System", labelKey: "system", apiType: "SYSTEM" },
    { label: "Node", labelKey: "node", apiType: "NODE" },
    { label: "Storage VM", labelKey: "storage_vm", apiType: "SVM" },
    { label: "Aggregate", labelKey: "local_tier", apiType: "AGGREGATE" },
    { label: "Volume", labelKey: "volume", apiType: "VOLUME" },
    { label: "LUN", labelKey: "lun", apiType: "LUN" }
];

export const DEFAULT_ONTAP_OBJECT_TYPES = ONTAP_OBJECT_TYPE_FILTERS.map(item => item.apiType);

const queryClause = searchTerm => {
    const term = String(searchTerm).replace(/'/g, "''");
    return `query eq '${term}'`;
};

export const getConsoleObjectsQuery = (searchTerm, limit, objectTypes = DEFAULT_ONTAP_OBJECT_TYPES) => {
    const types = (objectTypes?.length ? objectTypes : DEFAULT_ONTAP_OBJECT_TYPES).map(objectType => `'${objectType}'`).join(",");

    return {
        filter: `${queryClause(searchTerm)} and objectType in (${types})`,
        ...(typeof limit === "number" ? { limit } : {}),
        count: true
    };
};

export const getDocumentationSearchQuery = (searchTerm, limit = CONSOLE_OBJECTS_PREVIEW_LIMIT) => ({
    filter: `${queryClause(searchTerm)} and objectType eq 'DOCUMENTATION_PAGE'`,
    limit,
    count: true
});

export const getKnowledgeBaseSearchQuery = (searchTerm, limit = CONSOLE_OBJECTS_PREVIEW_LIMIT) => ({
    filter: `${queryClause(searchTerm)} and objectType eq 'KB_ARTICLE'`,
    limit,
    count: true
});

const getObjectType = result => {
    //the type may arrive as a plain type, an asset type (ONTAP_VOLUME) or a media type (application/vnd.netapp.volume)
    const rawType = result.objectType || result.resourceType || result.assetType || _.last(_.split(result.type, "."));
    const normalizedType = _.last(_.split(_.toLower(rawType), "_"))?.replace(/[^a-z]/g, "");

    return OBJECT_TYPE_LABELS[normalizedType] || (rawType ? _.startCase(_.toLower(rawType)) : null);
};

const STATUS_PRESENTATION = {
    reachable: ["Reachable", "success"],
    healthy: ["Healthy", "success"],
    unreachable: ["Unreachable", "error"],
    unknown: ["Unknown", "neutral"],
    up: ["Up", "success"],
    booting: ["Booting", "information"],
    down: ["Down", "error"],
    taken_over: ["Taken over", "information"],
    waiting_for_giveback: ["Waiting for giveback", "information"],
    degraded: ["Degraded", "warning"],
    starting: ["Starting", "information"],
    running: ["Running", "success"],
    stopping: ["Stopping", "warning"],
    stopped: ["Stopped", "neutral"],
    deleting: ["Deleting", "warning"],
    initializing: ["Initializing", "information"],
    online: ["Online", "success"],
    onlining: ["Onlining", "information"],
    offline: ["Offline", "neutral"],
    offlining: ["Offlining", "warning"],
    relocating: ["Relocating", "information"],
    unmounted: ["Unmounted", "neutral"],
    restricted: ["Restricted", "warning"],
    inconsistent: ["Inconsistent", "warning"],
    failed: ["Failed", "error"],
    error: ["Error", "error"],
    mixed: ["Mixed", "neutral"],
    foreign_lun_error: ["Foreign LUN error", "error"],
    nvfail: ["NVFail", "error"],
    space_error: ["Space error", "error"]
};

const statusKey = value => String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");

const statusPresentation = value => STATUS_PRESENTATION[statusKey(value)];

const objectTypeByLabel = Object.fromEntries(ONTAP_OBJECT_TYPE_FILTERS.map(item => [item.label, item]));

export const consoleObjectTypeLabel = (objectType, translate) => {
    const item = objectTypeByLabel[objectType];
    return item ? translate(`object_types.${item.labelKey}`) : objectType;
};

export const consoleObjectStatusLabel = (status, translate) => {
    if (!status) {
        return status;
    }
    const key = statusKey(status);
    return STATUS_PRESENTATION[key] ? translate(`statuses.${key}`) : status;
};

export const consoleObjectStatusDetail = (statusDetail, translate) => {
    if (typeof statusDetail !== "string") {
        return statusDetail;
    }
    const serial = statusDetail.match(/^Serial:\s*(.+)$/);
    return serial ? translate("serial_number", { serial: serial[1] }) : statusDetail;
};

export const getStatusTone = status => status ? (statusPresentation(status)?.[1] || "neutral") : null;

const rawStatusOf = result => result.health?.status || result.state || result.status || null;

const getStatus = rawStatus => {
    if (!rawStatus) {
        return null;
    }
    return statusPresentation(rawStatus)?.[0] || _.startCase(_.toLower(String(rawStatus).replace(/_/g, " ")));
};

const getStatusDetail = result => result.serialNumber ? `Serial: ${result.serialNumber}` : null;

const ANCESTOR_NAME_KEYS = ["fleet", "system", "cluster", "node", "svm", "aggregate", "volume", "lun"];
const ANCESTOR_TYPES = {
    FLEET: "FLEET",
    SYSTEM: "SYSTEM",
    CLUSTER: "SYSTEM",
    NODE: "NODE",
    SVM: "SVM",
    STORAGEVM: "SVM",
    AGGREGATE: "AGGREGATE",
    VOLUME: "VOLUME",
    LUN: "LUN"
};

const ancestorType = value => ANCESTOR_TYPES[String(value || "").toUpperCase().replace(/[^A-Z]/g, "")];

const parseAncestor = ancestor => {
    if (!_.isPlainObject(ancestor)) {
        return null;
    }
    const explicitType = ancestorType(ancestor.objectType || ancestor.type);
    if (explicitType) {
        return {
            type: explicitType,
            id: ancestor.id || ancestor.uuid || null,
            name: ancestor.displayName || ancestor.name || ancestor.title || null
        };
    }
    const key = ANCESTOR_NAME_KEYS.find(nameKey => ancestor[nameKey] && ancestorType(nameKey));
    if (!key) {
        return null;
    }
    const value = ancestor[key];
    if (_.isPlainObject(value)) {
        return {
            type: ancestorType(key),
            id: value.id || value.uuid || ancestor.id || null,
            name: value.displayName || value.name || null
        };
    }
    return {
        type: ancestorType(key),
        id: ancestor.id || ancestor.uuid || null,
        name: typeof value === "string" ? value : null
    };
};

const getAncestorValue = ancestor => {
    if (typeof ancestor === "string") {
        return ancestor;
    }
    return parseAncestor(ancestor)?.name || null;
};

const ancestorId = (ancestors, type) => ancestors.find(ancestor => ancestor.type === type)?.id || null;

const fleetNameOf = result => _.get(result, "fleets[0].name") || result.fleet?.name || result.fleetName || null;

const getPath = result => {
    const ancestorNames = _.isArray(result.ancestors)
        ? _.compact(result.ancestors.map(getAncestorValue))
        : _.isArray(result.path)
            ? _.compact(result.path)
            : _.compact([
                result.cluster?.name || result.clusterName || result.system?.name,
                result.svm?.name || result.svmName,
                result.volume?.name || result.volumeName
            ]);
    const fleetName = fleetNameOf(result);
    if (!fleetName || ancestorNames[0] === fleetName) {
        return ancestorNames;
    }
    return [fleetName, ...ancestorNames];
};

const itemLabel = item => item?.displayName || item?.title || item?.name;

export const mapSearchResultToConsoleObject = result => {
    const ancestors = _.isArray(result.ancestors) ? _.compact(result.ancestors.map(parseAncestor)) : [];
    const objectType = getObjectType(result);
    const id = result.id || null;
    const rawStatus = rawStatusOf(result);

    return {
        id,
        name: itemLabel(result),
        objectType,
        path: getPath(result),
        status: getStatus(rawStatus),
        statusTone: getStatusTone(rawStatus),
        statusDetail: getStatusDetail(result),
        fleetId: objectType === "Fleet" ? id : (ancestorId(ancestors, "FLEET") || result.fleetId || result.fleet?.id || null),
        clusterId: objectType === "System" ? id : (ancestorId(ancestors, "SYSTEM") || result.clusterId || result.cluster?.id || result.system?.id || result.systemId || null),
        workingEnvironmentId: result.workingEnvironmentId || null,
        clusterPersonality: result.clusterPersonality || null
    };
};

const REFERENCE_SECTIONS = {
    DOCUMENTATION: "NetApp Documentation",
    DOCUMENTATION_PAGE: "NetApp Documentation",
    KNOWLEDGE_BASE: "Knowledge Base",
    KB_ARTICLE: "Knowledge Base"
};

const referenceSection = item => REFERENCE_SECTIONS[item?.objectClass] || REFERENCE_SECTIONS[item?.objectType];

const referenceHref = item => {
    const href = _.find(item?.links, link => typeof link?.href === "string")?.href;
    if (href && (/^https?:\/\//.test(href) || href.startsWith("/"))) {
        return href;
    }
    return null;
};

const mapReferenceItem = item => ({
    id: item.id,
    label: itemLabel(item),
    name: itemLabel(item),
    href: referenceHref(item)
});

const mapReferenceItems = items => items
    .map(mapReferenceItem)
    .filter(item => typeof item.label === "string" && item.label);

const responseItems = data => _.find([data, data?.items, data?.searchResults, data?.results], _.isArray) || [];

export const mapSearchResponse = data => {
    const results = responseItems(data);

    return {
        results: results.map(mapSearchResultToConsoleObject),
        count: _.isNumber(data?.count) ? data.count : results.length
    };
};

export const mapUnifiedSearchResponse = data => {
    const items = responseItems(data);
    const referenceItems = items.filter(referenceSection);
    const usesPageShape = items.some(item => item?.displayName || item?.title || _.isArray(item?.ancestors));

    if (!referenceItems.length && !usesPageShape) {
        return { ...mapSearchResponse(data), documentationResults: null };
    }

    const consoleItems = items.filter(item => !referenceSection(item));
    const consoleCount = !referenceItems.length && _.isNumber(data?.count) ? data.count : consoleItems.length;

    return {
        results: consoleItems.map(mapSearchResultToConsoleObject),
        count: consoleCount,
        documentationResults: {
            "NetApp Documentation": mapReferenceItems(referenceItems.filter(item => referenceSection(item) === "NetApp Documentation")),
            "Knowledge Base": mapReferenceItems(referenceItems.filter(item => referenceSection(item) === "Knowledge Base"))
        }
    };
};
